import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import useAuthStore from '../store/authStore';
import { resourceService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { Card, Badge, Button, Modal, Input, Field, Select, Textarea, EmptyState, Spinner } from '../components/ui';
import { getApiErrorMessage } from '../utils/apiErrors';

const CATEGORIES = ['policy', 'form', 'material', 'other'];
const EMPTY_FORM = { title: '', description: '', category: 'other', sourceType: 'link', external_url: '', file: null, is_public: true };

function Resources() {
  const { user } = useAuthStore();
  const { addNotification } = useNotification();
  const confirm = useConfirm();
  const { t } = useTranslation();

  const canUpload = user?.roles?.some((r) => ['teacher', 'administrator'].includes(r.name?.toLowerCase()));
  const isAdmin = user?.roles?.some((r) => r.name?.toLowerCase() === 'administrator');

  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchResources = useCallback(() => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    resourceService.getResources(params)
      .then((res) => {
        const data = res.data;
        setResources(Array.isArray(data) ? data : data.results || []);
      })
      .catch(() => addNotification(t('Failed to load resources'), 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  useEffect(() => { fetchResources(); }, [fetchResources]);

  const openCreate = () => { setForm(EMPTY_FORM); setModalOpen(true); };

  const save = async () => {
    if (!form.title.trim()) {
      addNotification(t('Title is required'), 'error');
      return;
    }
    if (form.sourceType === 'link' && !form.external_url.trim()) {
      addNotification(t('Provide a link or switch to file upload'), 'error');
      return;
    }
    if (form.sourceType === 'file' && !form.file) {
      addNotification(t('Choose a file to upload'), 'error');
      return;
    }
    setSaving(true);
    try {
      await resourceService.createResource({
        title: form.title,
        description: form.description,
        category: form.category,
        is_public: form.is_public,
        external_url: form.sourceType === 'link' ? form.external_url : '',
        file: form.sourceType === 'file' ? form.file : null,
      });
      addNotification(t('Resource added'), 'success');
      setModalOpen(false);
      fetchResources();
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, 'Failed to save resource'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (resource) => {
    if (!(await confirm(t('Delete "{{name}}"?', { name: resource.title })))) return;
    try {
      await resourceService.deleteResource(resource.id);
      setResources((prev) => prev.filter((r) => r.id !== resource.id));
      addNotification(t('Resource deleted'), 'success');
    } catch (e) {
      addNotification(t('Failed to delete resource'), 'error');
    }
  };

  const canManage = (resource) => isAdmin || resource.uploaded_by?.id === user?.id;

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Resources')} subtitle={t('Shared documents, forms, and materials for the whole school')} />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setCategory('')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${category === '' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
              >
                {t('All')}
              </button>
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${category === c ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
                >
                  {t(c)}
                </button>
              ))}
            </div>
            {canUpload && <Button onClick={openCreate}>+ {t('Add Resource')}</Button>}
          </div>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : resources.length === 0 ? (
            <Card><EmptyState title={t('No resources yet')} action={canUpload ? <Button onClick={openCreate}>+ {t('Add Resource')}</Button> : null} /></Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {resources.map((r) => (
                <Card key={r.id} className="flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <Badge tone="blue">{t(r.category)}</Badge>
                    {!r.is_public && <Badge tone="gray">{t('Private')}</Badge>}
                  </div>
                  <h3 className="font-display font-bold text-gray-900 mb-1">{r.title}</h3>
                  {r.description && <p className="text-sm text-gray-600 flex-1 line-clamp-3">{r.description}</p>}
                  <p className="text-xs text-gray-400 mt-2">
                    {t('Uploaded by')} {r.uploaded_by?.first_name} {r.uploaded_by?.last_name}
                  </p>
                  <div className="mt-4 flex items-center justify-between gap-2">
                    <a
                      href={r.file || r.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-primary-600 hover:text-primary-700"
                    >
                      {r.file ? t('Download') : t('Open Link')}
                    </a>
                    {canManage(r) && (
                      <button onClick={() => remove(r)} className="text-xs text-gray-400 hover:text-red-500">
                        {t('Delete')}
                      </button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t('Add Resource')}
        footer={<>
          <Button variant="secondary" onClick={() => setModalOpen(false)}>{t('Cancel')}</Button>
          <Button loading={saving} onClick={save}>{t('Save')}</Button>
        </>}
      >
        <div className="space-y-4">
          <Field label={t('Title')} required>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} autoFocus />
          </Field>
          <Field label={t('Description')}>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <Field label={t('Category')}>
            <Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{t(c)}</option>)}
            </Select>
          </Field>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setForm({ ...form, sourceType: 'link' })}
              className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium border ${form.sourceType === 'link' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'border-gray-200 text-gray-500'}`}
            >
              {t('Link')}
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, sourceType: 'file' })}
              className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium border ${form.sourceType === 'file' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'border-gray-200 text-gray-500'}`}
            >
              {t('Upload File')}
            </button>
          </div>

          {form.sourceType === 'link' ? (
            <Field label={t('URL')} required>
              <Input value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="https://..." />
            </Field>
          ) : (
            <Field label={t('File')} required>
              <input
                type="file"
                onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
              />
            </Field>
          )}

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} />
            {t('Visible to everyone (uncheck to keep private to you and admins)')}
          </label>
        </div>
      </Modal>
    </div>
  );
}

export default Resources;
